import { useState } from 'react';
import {
  Box,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KindChip } from '@/components/KindChip';
import { MoneyParts, MoneyText } from '@/components/MoneyText';
import { StatusChip } from '@/components/StatusChip';
import { KebabIcon } from '@/components/icons';
import { ObjectMenu } from '@/shell/OverflowMenu';
import type { Series, SeriesStatus, TargetView } from '@/api/planned';
import { bpToPercentText, formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';
import { labelCanvasSx } from '@/theme/textLabel';
import { describeRecurrence } from './recurrence/recurrence';

type StatusFilter = SeriesStatus | 'ALL';

interface SeriesListProps {
  rows: Series[];
  currencyByAccount: Record<number, string | null>;
  /** Each account's name, for the row's "{frequency} · {account}" line. */
  accountNames: Record<number, string>;
  defaultCurrency: string | null;
  callerName: string;
  subscriber: boolean;
  expandedId: number | null;
  onExpand: (id: number | null) => void;
  onEdit: (series: Series) => void;
  onCancel: (series: Series) => void;
  onEndFrom: (series: Series) => void;
  onFilter: (status: StatusFilter) => void;
  statusFilter: StatusFilter;
}

/** An allocation chip's text: "{pct}% → {envelope}", "{fixed} → {envelope}" or "Remainder → {envelope}". */
function AllocationChip({ target, currency }: { target: TargetView; currency: string | null }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const envelope = target.envelopeName ?? t('planned.v2.envelopeUnnamed');
  const share =
    target.mode === 'PERCENT' && target.percentBp !== null ? (
      bpToPercentText(target.percentBp, locale)
    ) : target.mode === 'FIXED' && target.fixedAmountMinor !== null ? (
      <MoneyParts minor={Math.abs(target.fixedAmountMinor)} currency={currency} />
    ) : (
      t('planned.v2.remainder')
    );
  const chipSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    p: '3px 9px',
    borderRadius: '999px',
    border: '1px solid',
    borderColor: tokens.line,
    // Contract v2 §8 (TMMAPP-0154 item 16): `successText` with a `line` border, on its guaranteed
    // wash (`successSoft`, the prototype's sage), as the register's chips.
    bgcolor: tokens.successSoft,
    color: tokens.successText,
    fontSize: 12,
    lineHeight: '17px',
    fontVariantNumeric: 'tabular-nums',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  } as const;
  const content = (
    <>
      {share}
      <span aria-hidden>→</span>
      {envelope}
    </>
  );
  // Each chip opens its envelope (visual contract v2 §8) when its virtual account is served.
  return target.virtualAccountId !== null ? (
    <Box
      component={RouterLink}
      to={`/v/${target.virtualAccountId}/e/${target.envelopeId}`}
      data-allocation-chip=""
      sx={{
        ...chipSx,
        '&:hover': { borderColor: tokens.brandBoundary },
        '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
      }}
    >
      {content}
    </Box>
  ) : (
    <Box component="span" data-allocation-chip="" sx={chipSx}>
      {content}
    </Box>
  );
}

/**
 * Recurring's list (TMMAPP-0009; the v2 rows, TMMAPP-0151 §H 23, prototype state 42, P:635–647): one
 * card of rows, each with the name (600) over "{frequency} · {account}" (`describeRecurrence`, the
 * product's one rule), the allocation chips (each opening its envelope), and the served signed amount
 * with "Next {date}" from the served `nextOccursOn` (TMMAPI-0186 U3; left out when there's none).
 * Pressing the name opens the row's details — payee, kind, auto-post, status and author — and a
 * subscriber's kebab holds Edit, End from and Cancel.
 */
export function SeriesList({
  rows,
  currencyByAccount,
  accountNames,
  defaultCurrency,
  callerName,
  subscriber,
  expandedId,
  onExpand,
  onEdit,
  onCancel,
  onEndFrom,
  onFilter,
  statusFilter,
}: SeriesListProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const label = tokens.textLabel('surface');
  const [menu, setMenu] = useState<{ series: Series; el: HTMLElement } | null>(null);

  return (
    <Stack spacing={1.5}>
      <FormControl size="small" sx={{ minWidth: 160, alignSelf: 'flex-start' }}>
        <InputLabel id="series-status">{t('planned.filterStatus')}</InputLabel>
        <Select
          labelId="series-status"
          label={t('planned.filterStatus')}
          value={statusFilter}
          onChange={(event) => onFilter(event.target.value as StatusFilter)}
        >
          <MenuItem value="ACTIVE">{t('status.series.ACTIVE')}</MenuItem>
          <MenuItem value="ENDED">{t('status.series.ENDED')}</MenuItem>
          <MenuItem value="CANCELLED">{t('status.series.CANCELLED')}</MenuItem>
          <MenuItem value="ALL">{t('planned.filterAll')}</MenuItem>
        </Select>
      </FormControl>
      {rows.length === 0 ? (
        <Typography>{t('planned.emptySeries')}</Typography>
      ) : (
        <Paper
          component="ul"
          aria-label={t('planned.seriesTitle')}
          sx={{
            listStyle: 'none',
            m: 0,
            p: 0,
            overflow: 'hidden',
            ...labelCanvasSx(tokens, 'surface'),
          }}
        >
          {rows.map((series, index) => {
            const currency = currencyByAccount[series.accountId] ?? defaultCurrency;
            const account = accountNames[series.accountId];
            const frequency = describeRecurrence(series.recurrence, locale, t);
            const expanded = expandedId === series.id;
            const detailsId = `series-${series.id}-details`;
            return (
              <Box
                component="li"
                key={series.id}
                data-series-row={series.id}
                sx={{
                  borderTop: index === 0 ? 'none' : '1px solid',
                  borderColor: tokens.line,
                  p: { xs: '14px 16px', sm: '16px 20px' },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    gap: '10px 20px',
                  }}
                >
                  <Box
                    sx={{
                      flex: '1 1 220px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                      minWidth: 0,
                    }}
                  >
                    <Box
                      component="button"
                      type="button"
                      aria-expanded={expanded}
                      aria-controls={detailsId}
                      onClick={() => onExpand(expanded ? null : series.id)}
                      sx={{
                        p: 0,
                        border: 0,
                        background: 'none',
                        cursor: 'pointer',
                        font: 'inherit',
                        fontWeight: 600,
                        fontSize: 15,
                        lineHeight: '21px',
                        color: tokens.text,
                        textAlign: 'start',
                        overflowWrap: 'anywhere',
                        '&:hover': { textDecoration: 'underline' },
                        '&:focus-visible': {
                          outline: `2px solid ${tokens.focusRing}`,
                          outlineOffset: 2,
                        },
                      }}
                    >
                      {series.name}
                    </Box>
                    <Typography
                      component="span"
                      sx={{ fontSize: 12.5, lineHeight: '18px', color: label }}
                    >
                      {account !== undefined
                        ? t('planned.v2.frequencyAccount', { frequency, account })
                        : frequency}
                    </Typography>
                  </Box>
                  {series.targets.length > 0 && (
                    <Box
                      sx={{
                        flex: '2 1 280px',
                        display: 'flex',
                        gap: '6px',
                        flexWrap: 'wrap',
                        minWidth: 0,
                      }}
                    >
                      {series.targets.map((target) => (
                        <AllocationChip key={target.id} target={target} currency={currency} />
                      ))}
                    </Box>
                  )}
                  <Box
                    sx={{
                      flex: '0 0 auto',
                      marginInlineStart: 'auto',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '2px',
                      }}
                    >
                      <Box component="span" sx={{ '& .MuiTypography-root': { fontWeight: 600 } }}>
                        <MoneyText
                          minor={series.amountMinor}
                          currency={currency}
                          signed
                          tone="register"
                        />
                      </Box>
                      {series.nextOccursOn !== null && (
                        <Typography
                          component="span"
                          data-series-next=""
                          sx={{
                            fontSize: 12,
                            lineHeight: '17px',
                            color: label,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {t('planned.v2.next', {
                            date: formatCivilDate(series.nextOccursOn, locale, {
                              month: 'short',
                              day: 'numeric',
                            }),
                          })}
                        </Typography>
                      )}
                    </Box>
                    {subscriber && (
                      <IconButton
                        size="small"
                        aria-label={t('planned.seriesActions')}
                        aria-haspopup="menu"
                        onClick={(event) => setMenu({ series, el: event.currentTarget })}
                        sx={{ marginBlockStart: '-4px', marginInlineEnd: '-8px' }}
                      >
                        <KebabIcon />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                <Collapse in={expanded} id={detailsId}>
                  <Stack
                    direction="row"
                    data-series-details=""
                    sx={{ gap: 0.75, flexWrap: 'wrap', alignItems: 'center', pt: 1.25 }}
                  >
                    {series.payee !== null && (
                      <Typography component="span" sx={{ fontSize: 13, color: tokens.text2 }}>
                        {t('planned.v2.payee', { payee: series.payee })}
                      </Typography>
                    )}
                    <KindChip neutral label={t(`planned.kind.${series.kind}`)} />
                    {series.fulfilment === 'AUTO_POST' && (
                      <KindChip neutral label={t('planned.autoPost')} />
                    )}
                    <StatusChip domain="series" status={series.status} />
                    {series.authorDisplayName !== callerName && (
                      <Typography component="span" sx={{ fontSize: 12, color: label }}>
                        {t('planned.author', { name: series.authorDisplayName })}
                      </Typography>
                    )}
                  </Stack>
                </Collapse>
              </Box>
            );
          })}
        </Paper>
      )}
      <ObjectMenu
        title={menu?.series.name}
        anchorEl={menu?.el}
        open={menu !== null}
        onClose={() => setMenu(null)}
      >
        <MenuItem
          onClick={() => {
            if (menu) onEdit(menu.series);
            setMenu(null);
          }}
        >
          {t('planned.editSeries')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menu) onEndFrom(menu.series);
            setMenu(null);
          }}
        >
          {t('planned.endFrom')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menu) onCancel(menu.series);
            setMenu(null);
          }}
        >
          {t('planned.cancelSeries')}
        </MenuItem>
      </ObjectMenu>
    </Stack>
  );
}
